import * as React from "react";
import { useEffect, useState, useContext, useMemo } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import Select from "react-select";

// react-bootstrap components
import {
  Card,
  Container,
  Row,
  Form,
  Col,
  Button,
  Spinner,
  Dropdown,
  DropdownButton,
} from "react-bootstrap";
import { DateObject } from "react-multi-date-picker";

import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";
import CustomMultiDateSelect from "components/Custom/CustomMultiDateSelect";
import SupportEvents from "views/SupportEvents/SupportEventList";
import CustomStatus from "components/Custom/CustomStatus";
import ChannelThreshold from "views/Clients/Environments/ConfigureChannels/ChannelThreshold";
import {
  tourGuideSteps,
  clientEnvironmentActionModalSteps,
  clientEnvironmentDefaultThresholdModalSteps,
} from "views/Clients/ClientSteps";
import UpdateMirthConnectConfiguration from "views/Clients/Environments/ConfigureEnvironment/UpdateMirthConnectConfiguration";
import {
  getList,
  getClient,
  postClientEnvironmentActionRequest,
  deleteClient,
  updateClientName,
  getChannelDefaultThreshold,
  getClientEnvironmentDefaultThreshold,
  updateClientEnvironmentDefaultThreshold,
  updateChannelDefaultThreshold,
  updateAllChannelsToDefaultThreshold,
  updateAllClientEnvironmentsToDefaultThreshold,
  getFavoriteChannels,
  invokeMCCExportMessagesTask
} from "graphql/queries.js";
import _ from "lodash";
import { API, graphqlOperation } from "aws-amplify";
import {
  convertDateListToString,
  convertDateListToDateRangeString,
} from "utilities/dateUtilities";
import { Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { isStatusInAccessibleState } from "utilities/environmentUtilities";
import SweetAlert from "react-bootstrap-sweetalert";
import { AuthContext } from "contexts/authContext";
import { useTour } from "@reactour/tour";
import CustomTooltip from "components/Custom/CustomTooltip";
import {
  MultiValueWithCustomTooltip,
  customEllipsisMultiSelectStyles,
} from "components/Custom/CustomMultiSelect.js";

function Client() {
  // Hooks
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const history = useHistory();

  const { clientId } = useParams();
  const getLastEvaluated = (difference) => {
    let lastEvaluated = "Just now";
    const day = Math.floor(difference / (1000 * 60 * 60 * 24));
    if (day === 0) {
      difference = difference % (1000 * 60 * 60 * 24);
      const hour = Math.floor(difference / (1000 * 60 * 60));
      if (hour === 0) {
        difference = difference % (1000 * 60 * 60);
        const mint = Math.floor(difference / (1000 * 60));
        if (mint > 0) {
          lastEvaluated = mint === 1 ? `${mint} min ago` : `${mint} mins ago`;
        }
      } else {
        lastEvaluated = hour === 1 ? `${hour} hour ago` : `${hour} hours ago`;
      }
    } else {
      lastEvaluated = day === 1 ? `${day} day ago` : `${day} days ago`;
    }
    return lastEvaluated;
  };

  const clientEnvironmentActionOptionsList = [
    { key: "none", value: "No Action" },
    { key: "suppress", value: "Block Email Alerts" },
    { key: "unsuppress", value: "Allow Email Alerts" },
    { key: "export", value: "Export Messages" },
    { key: "db", value: "Delete Database Entries" },
    {
      key: "resource",
      value: "Delete Cloud Infrastructure (Fully Managed Only)",
    },
    {
      key: "all",
      value:
        "Delete Cloud Infrastructure and Database Entries (Fully Managed Only)",
    },
  ];

  const clientThresholdTypes = [
    {
      key: "Client Environment Default Thresholds",
      value: "Client Environment Default Thresholds",
    },
    { key: "Channel Default Thresholds", value: "Channel Default Thresholds" },
  ];

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  const [multiSelected, setMultiSelected] = useState([]);
  const [alert, setAlert] = useState(null);
  const [
    clientEnvironmentActionModalOpen,
    setClientEnvironmentActionModalOpen,
  ] = useState(false);
  const [clientThresholdsModalOpen, setClientThresholdsModalOpen] =
    useState(false);
  const [clientThresholdType, setClientThresholdType] = useState("");
  const [clientData, setClientData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [jobRanAt, setJobRanAt] = useState(null);
  // const [activityFeedTableData, setActivityFeedTableData] = useState([]);
  // const [startDate, setStartDate] = useState(moment().subtract(1, "day"));
  // const [endDate, setEndDate] = useState(moment());
  const [showLoader, setShowLoader] = useState(false);
  const [showFirstLoader, setShowFirstLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [metricDateList, setMetricDateList] = useState([new DateObject()]);
  const groupName = currentUser.groupName;
  const [allEnvReady, setAllEnvReady] = useState(true);
  // const [nameEdit, setNameEdit] = useState(false);
  const [clientNameValue, setClientNameValue] = useState("");
  const [channelThreshold, setChannelThreshold] = useState({});
  const [channelThresholdBackup, setChannelThresholdBackup] = useState({});
  const [clientEnvironmentThreshold, setClientEnvironmentThreshold] = useState(
    {}
  );
  const [
    clientEnvironmentThresholdBackup,
    setClientEnvironmentThresholdBackup,
  ] = useState({});
  const [modalSpinner, setModalSpinner] = useState(false);
  const [
    clientEnvironmentThresholdChanged,
    setClientEnvironmentThresholdChanged,
  ] = useState(false);
  const [channelThresholdChanged, setChannelThresholdChanged] = useState(false);
  const [channelThresholdExists, setChannelThresholdExists] = useState(false);
  const [
    clientEnvironmentThresholdExists,
    setClientEnvironmentThresholdExists,
  ] = useState(false);
  const [showInactiveEnvironments, setShowInactiveEnvironments] =
    useState(false);
  const [selectedEnvironments, setSelectedEnvironments] = useState([]);
  const [activeEnvironmentOptions, setActiveEnvironmentOptions] = useState([]);
  const [inactiveEnvironmentOptions, setInactiveEnvironmentOptions] = useState(
    []
  );
  const [favoriteChannelsData, setFavoriteChannelsData] = useState([]);
  const handleIncludeInactiveClientEnvironments = () => {
    setSelectedEnvironments([]);
    setShowInactiveEnvironments(!showInactiveEnvironments);
  };

  const authTypeMap = {
    BASIC: "BASIC",
    API_KEY: "API KEY",
    MC_AUTH: "MC AUTH",
  };

  const mapSupportTier = (level) => {
    switch (level) {
      case 1:
        return "Low";
      case 2:
        return "Medium";
      case 3:
        return "Critical";
      default:
        return "N/A";
    }
  };
  const actionOptionsList =
    groupName === "internal"
      ? clientEnvironmentActionOptionsList
      : clientEnvironmentActionOptionsList.slice(0, -3);
  const actionOptionsListInternalEnvironment =
    groupName === "internal"
      ? clientEnvironmentActionOptionsList
      : clientEnvironmentActionOptionsList.slice(0, -3);
  const actionOptionsListExternalEnvironment =
    clientEnvironmentActionOptionsList.slice(0, -3);

  /* const iconClass = {
    color: "#23ccef",
    position: "relative",
    left: "10px",
    fontSize: "20px",
    cursor: "pointer",
  };
  const iconClass1 = {
    color: "#fb414b",
    position: "relative",
    left: "25px",
    fontSize: "20px",
    cursor: "pointer",
  };
  const inputClass = {
    all: "unset",
    backgroundColor: "transparent",
    border: "1px solid #000000",
    borderRadius: "3px",
    padding: "3px",
    minWidth: `${clientNameValue?.length}ch`,
  }; */

  const headers = [
    { key: "client_environment_status", label: "Client Environment Status" },
    { key: "client_environment_name", label: "Client Environment Name" },
    { key: "deploy_status", label: "Deploy Status" },
    { key: "suppress_support_event", label: "Blocked Email Alerts" },
    { key: "spoke_name", label: "Spoke Name" },
    { key: "spoke_type", label: "Spoke Type" },
    { key: "region", label: "Region" },
    { key: "aws_account", label: "Aws Account" },
    { key: "last_calculated", label: "Last Calculated" },
  ];
  const favoriteHeaders = [
    { key: "status", label: "Status" },
    { key: "support_tier", label: "Support Tier" },
    { key: "channel_group_name", label: "Group" },
    { key: "client_environment_name", label: "Client Environment Name" },
    { key: "channel_name", label: "Channel Name" },
    { key: "suppress_support_event", label: "Blocked Email Alerts" },
    { key: "received_messages", label: "Received" },
    { key: "queued_messages", label: "Queued" },
    { key: "filtered_messages", label: "Filtered" },
    { key: "errored_messages", label: "Errored" },
    { key: "sent_messages", label: "Sent/Transformed" },
    { key: "avg_process_time", label: "Avg Process Time (sec)" },
    { key: "mc_auth_type", label: "Auth Type" },
    { key: "last_reviewed_by_support", label: "Last Reviewed" },
  ];
  const metricsHeaders = [
    { key: "client_environment_status", label: "Client Environment Status" },
    { key: "client_environment_name", label: "Client Environment Name" },
    { key: "channels_count", label: "Channels Count" },
    { key: "received_messages", label: "Received Messages" },
    { key: "queued_messages", label: "Queued Messages" },
    { key: "filtered_messages", label: "Filtered Messages" },
    { key: "sent_messages", label: "Sent Messages" },
    { key: "errored_messages", label: "Errored Messages" },
    { key: "avg_process_time", label: "Avg Process Time" },
  ];

  // Pagination Starts
  const columnMapping = {
    Status: "client_environment_status",
    "Environment Id": "client_environment_id",
    Environment: "client_environment_name",
    "Deploy Status": "deploy_status",
    "Blocked Email Alerts": "suppress_support_event",
    "Spoke Name": "spoke_name",
    "Spoke Type": "spoke_type",
    Region: "region",
    "AWS Account": "aws_account",
    "Last Environment Update": "created_at",
    Channels: "channels_count",
    Received: "received_messages",
    Queued: "queued_messages",
    Filtered: "filtered_messages",
    Errored: "errored_messages",
    "Sent/Transformed": "sent_messages",
    "Avg Process Time (sec)": "avg_process_time",
  };
  const [totalCount, setTotalCount] = useState(0);
  const [totalChannelCount, setTotalChannelCount] = useState(0);
  const numberOfRowsData = [25, 50, 100];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[1]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
  const [sortValue, setSortValue] = useState("Environment Id");

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  // Favorite Channels Pagination Starts
  const favoriteColumnMapping = {
    Status: "status",
    "Support Tier": "support_tier",
    Group: "channel_group_name",
    "Client Environment Name": "client_environment_name",
    "Channel Name": "channel_name",
    "Blocked Email Alerts": "suppress_support_event",
    Received: "received_messages",
    Queued: "queued_messages",
    Filtered: "filtered_messages",
    Errored: "errored_messages",
    "Sent/Transformed": "sent_messages",
    "Avg Process Time (sec)": "avg_process_time",
    "Auth Type": "mc_auth_type",
    "Last Reviewed": "last_reviewed_by_support",
  };

  const [favTotalCount, setFavTotalCount] = useState(0);
  const [favCurrentLimit, setFavCurrentLimit] = useState(numberOfRowsData[1]);
  const [favCurrentOffset, setFavCurrentOffset] = useState(0);
  const [favAscOrDescValue, setFavAscOrDescValue] = useState("ASC");
  const [favSortValue, setFavSortValue] = useState("Channel Name");

  const setFavStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setFavCurrentLimit(value);
    } else if (key === "offset") {
      setFavCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setFavAscOrDescValue(value);
    } else {
      setFavSortValue(value);
    }
  };
  // Favorite Channels Pagination Ends

  // Fetch options for client environments
  const fetchList = async (is_active) => {
    try {
      if (groupName !== "internal" && !is_active) return true;
      const response = await API.graphql(
        graphqlOperation(getList, {
          type: "ENVIRONMENTS",
          id: clientId,
          auth_client_id: currentUser.clientId,
          is_active,
        })
      );
      if (response.data?.getList?.success) {
        const options = response.data.getList
          ? response.data.getList.body
            .map((environment) => {
              const option = {
                value: environment.id,
                label: environment.name,
              };
              return option;
            })
            .sort((a, b) => a.label.localeCompare(b.label))
          : [];
        if (is_active) {
          setActiveEnvironmentOptions(options);
        } else {
          setInactiveEnvironmentOptions(options);
        }
      } else {
        console.error(response.data?.getList?.message);
        notify("danger", `Could not fetch environments list.`);
      }
      return response.data?.getList?.success;
    } catch (error) {
      console.error(error);
      notify("danger", `Could not fetch environments list.`);
      return false;
    }
  };

  const fetchAllFavoriteChannels = async (variables = {}, isCSVDownload = false) => {
    try {
      setShowLoader(true);
      const params = {
        client_id: clientId,
        metric_date_range_list: convertDateListToDateRangeString(metricDateList),
      };
      variables = { ...variables, ...params };
      const response = await API.graphql(
        graphqlOperation(getFavoriteChannels, variables)
      );
      if (!isCSVDownload) {
        if (response.data?.getFavoriteChannels?.success) {
          const favoriteChannels = response.data.getFavoriteChannels.body?.favorite_channels || [];
          setFavoriteChannelsData(favoriteChannels);
          setShowLoader(false);
        } else {
          console.error(response.data?.getFavoriteChannels?.message);
          notify("danger", `Could not fetch Favorite Channels.`);
        }
      } else {
        const filter = {
          "Client Id": clientId,
        };
        const messageDataForCSV = _.get(
          response,
          "data.getFavoriteChannels.body.favorite_channels"
        );
        return { messageDataForCSV, filter };
      }
      return response.data?.getFavoriteChannels?.success;
    } catch (error) {
      console.error("Error fetching all favorite channels:", error);
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  const fetchClient = async (variables = {}, isCSVDownload = false) => {
    try {
      setShowLoader(true);
      const params = {
        client_id: clientId,
        metric_date_range_list:
          convertDateListToDateRangeString(metricDateList),
        is_active: !showInactiveEnvironments,
        client_environment_ids: selectedEnvironments.map(
          (environment) => environment.value
        ),
      };
      variables = { ...variables, ...params };
      const response = await API.graphql(
        graphqlOperation(getClient, variables)
      );
      if (response.data?.getClient?.success) {
        if (!isCSVDownload) {
          const clientData = _.get(response, "data.getClient.body", []);
          const data = _.get(
            response,
            "data.getClient.body.client_environments_body",
            []
          );
          const totalCount = _.get(
            response,
            "data.getClient.body.client_environments_body.total_count",
            0
          );
          setClientNameValue(clientData?.client_name);
          setTotalCount(totalCount);
          setClientData(clientData);
          if (data.client_environments.length > 0) {
            setJobRanAt(data.client_environments[0].last_calculated);
          } else {
            setJobRanAt(null);
          }
          const now = new Date();
          const environments = data.client_environments.map(
            (environmentData) => {
              const lastUpdated = new Date(
                environmentData.updated_at || environmentData.created_at
              );
              const difference = now - lastUpdated;
              const lastEvaluated = getLastEvaluated(difference);
              if (
                allEnvReady &&
                !isStatusInAccessibleState(environmentData.deploy_status)
              ) {
                setAllEnvReady(false);
              }
              setTotalChannelCount(
                (prevCount) => prevCount + environmentData.channels_count
              );
              return {
                ...environmentData,
                lastEvaluated,
              };
            }
          );
          setTableData(environments);
        } else {
          const filter = {
            "Client Id": clientId,
            "Metric Date List": convertDateListToString(metricDateList),
          };
          const messageDataForCSV = _.get(
            response,
            "data.getClient.body.client_environments_body.client_environments"
          );
          return { messageDataForCSV, filter };
        }
      } else {
        console.error(response.data?.getClient?.message);
      }
      return response.data?.getClient?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  /* const changeClientName = async () => {
    try {
      setAlert(
        <SweetAlert
          showCancel
          title={"Are you sure?"}
          onConfirm={async () => {
            setShowLoader(true);
            const variables = {
              client_id: clientId,
              client_name: clientNameValue,
            };
            const response = await API.graphql(
              graphqlOperation(updateClientName, variables)
            );
            if (response.data?.updateClientName?.success) {
              notify(
                "success",
                response.data?.updateClientName?.body?.response
              );
              const filterObj = {
                offSet: 0,
                limit: currentLimit,
              };
              await fetchClient(filterObj);
              setNameEdit(false);
              setAlert(null);
            } else {
              console.error(response.data?.updateClientName?.message);
            }
            return response.data?.updateClientName?.success;
          }}
          onCancel={() => setAlert(null)}
          confirmBtnBsStyle="primary"
          cancelBtnBsStyle="danger"
          confirmBtnText="Confirm"
          cancelBtnText="Cancel"
        >
          You want to update the client's name to "{clientNameValue}" ?
        </SweetAlert>
      );
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setShowLoader(false);
    }
  }; */

  function resetSelect(num_select, defaultSelect) {
    let new_list = [];
    for (
      let num_select_index = 0;
      num_select_index < num_select;
      num_select_index++
    ) {
      new_list.push(actionOptionsList[defaultSelect]);
    }
    setMultiSelected(new_list);
  }

  function handleMultiSelect(key, event, index) {
    let new_list = [];
    for (
      let select_index = 0;
      select_index < multiSelected.length;
      select_index++
    ) {
      if (select_index == index) {
        new_list.push({ key: key, value: event.target.text });
      } else {
        new_list.push(multiSelected[select_index]);
      }
    }
    setMultiSelected(new_list);
  }

  function handleClientEnvironmentActionAllSelect(key, client_env_list) {
    let selected_index = clientEnvironmentActionOptionsList.findIndex(
      (x) => x.key === key
    );
    let new_list = [];
    for (
      let client_env_index = 0;
      client_env_index < client_env_list.length;
      client_env_index++
    ) {
      if (
        client_env_list[client_env_index].spoke_type != 0 &&
        selected_index > 3
      ) {
        new_list.push(multiSelected[client_env_index]);
      } else {
        new_list.push(clientEnvironmentActionOptionsList[selected_index]);
      }
      setMultiSelected(new_list);
    }
  }

  const handleClientEnvironmentActionSubmit = async (
    client_id,
    client_environment_list,
    user_id
  ) => {
    let payload = {};
    payload.client_id = client_id;
    payload.user_id = user_id;

    let suppress_list = [];
    let unsuppress_list = [];
    let delete_resource_list = [];
    let delete_db_hub_list = [];
    let delete_db_spoke_list = [];
    let export_messages_env_list = [];

    for (
      let clientEnvironmentActionOptionIndex = 0;
      clientEnvironmentActionOptionIndex < multiSelected.length;
      clientEnvironmentActionOptionIndex++
    ) {
      if (multiSelected[clientEnvironmentActionOptionIndex].key === "suppress") {
        suppress_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
      } else if (
        multiSelected[clientEnvironmentActionOptionIndex].key === "unsuppress"
      ) {
        unsuppress_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
      } else if (
        multiSelected[clientEnvironmentActionOptionIndex].key === "resource"
      ) {
        delete_resource_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
      } else if (
        multiSelected[clientEnvironmentActionOptionIndex].key === "db"
      ) {
        delete_db_hub_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
        if (
          client_environment_list[clientEnvironmentActionOptionIndex]
            .spoke_type === 0
        ) {
          delete_db_spoke_list.push(
            client_environment_list[clientEnvironmentActionOptionIndex]
              .client_environment_id
          );
        }
      } else if (
        multiSelected[clientEnvironmentActionOptionIndex].key === "export"
      ) {
        export_messages_env_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
      } else if (
        multiSelected[clientEnvironmentActionOptionIndex].key === "all"
      ) {
        delete_resource_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
        delete_db_hub_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
        delete_db_spoke_list.push(
          client_environment_list[clientEnvironmentActionOptionIndex]
            .client_environment_id
        );
      }
    }

    payload.suppress_list = suppress_list;
    payload.unsuppress_list = unsuppress_list;
    payload.delete_resource_list = delete_resource_list;
    payload.delete_db_hub_list = delete_db_hub_list;
    payload.delete_db_spoke_list = delete_db_spoke_list;

    try {
      if (
        suppress_list.length === 0 &&
        unsuppress_list.length === 0 &&
        delete_resource_list.length === 0 &&
        delete_db_hub_list.length === 0 &&
        export_messages_env_list.length === 0
      ) {
        notify(
          "info",
          "Client Environment request was not sent because no actions were changed.",
        );
        return;
      } 
      if (export_messages_env_list.length !== 0) {
        const response = await API.graphql(
          graphqlOperation(invokeMCCExportMessagesTask, {
            environment_ids: export_messages_env_list,
          }),
        );

        const result = response.data.invokeMCCExportMessagesTask;

        if (result?.success && result?.body) {
          const { cloud_task_ids, skipped_environments } = result.body;

          // Show success notification if there are cloud_task_ids
          if (cloud_task_ids && cloud_task_ids.length > 0) {
            const taskIdsStr = cloud_task_ids.join(", ");
            notify(
              "success",
              `Export messages task created successfully. Task IDs: ${taskIdsStr}`,
              -1,
            );
          }

          // Show danger notification if there are skipped environments
          if (skipped_environments && skipped_environments.length > 0) {
            const skippedEnvsNames = skipped_environments
              .map((env) => env.environment_name)
              .join(", ");
            notify(
              "danger",
              `Your export request was not created. Please retry the request after ${skippedEnvsNames} pending tasks have been completed.`,
              -1,
            );
          }

          // If no tasks were created and no environments were skipped, show a generic warning message
          if (
            (!cloud_task_ids || cloud_task_ids.length === 0) &&
            (!skipped_environments || skipped_environments.length === 0)
          ) {
            notify(
              "warning",
              "No export messages tasks were created nor any environments were skipped.",
            );
          }
        } else {
          notify("danger", "Failed to create export messages task");
        }
      }
      if (
        suppress_list.length !== 0 ||
        unsuppress_list.length !== 0 ||
        delete_resource_list.length !== 0 ||
        delete_db_hub_list.length !== 0
      ) {
        const response = await API.graphql(
          graphqlOperation(postClientEnvironmentActionRequest, payload),
        );

        if (response.data?.postClientEnvironmentActionRequest?.success) {
          notify(
            "success",
            "Successfully sent client environment action request.",
          );
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        } else {
          notify(
            "warning",
            "Failed to send client environment action request.",
          );
        }
      }
    } catch (e) {
      notify(
        "warning",
        "Failed to get response after client environment action request."
      );
    }
  };

  const functionToChangeClientEnvironmentThreshold = async () => {
    setAlert(
      <SweetAlert
        showCancel
        title={"Are you sure?"}
        onConfirm={async () => {
          try {
            setAlert(null);
            setModalSpinner(true);
            let params = {
              client_id: clientId,
            };
            params = { ...clientEnvironmentThreshold, ...params };
            const response = await API.graphql(
              graphqlOperation(
                updateAllClientEnvironmentsToDefaultThreshold,
                params
              )
            );
            if (
              response.data?.updateAllClientEnvironmentsToDefaultThreshold
                ?.success
            ) {
              notify(
                "success",
                "Successfully update default channel threshold values."
              );
            } else {
              console.error(
                response.data?.updateAllClientEnvironmentsToDefaultThreshold
                  ?.message
              );
            }
          } catch (error) {
            console.error(error);
            return false;
          } finally {
            setModalSpinner(false);
          }
        }}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="primary"
        cancelBtnBsStyle="danger"
        confirmBtnText="Confirm"
        cancelBtnText="Cancel"
      >
        You want to Apply the default values to all Client Environments?
      </SweetAlert>
    );
  };

  const functionToChangeChannelThreshold = async () => {
    setAlert(
      <SweetAlert
        showCancel
        title={"Are you sure?"}
        onConfirm={async () => {
          try {
            setAlert(null);
            setModalSpinner(true);
            let params = {
              client_id: clientId,
            };
            params = { ...channelThreshold, ...params };
            const response = await API.graphql(
              graphqlOperation(updateAllChannelsToDefaultThreshold, params)
            );
            if (response.data?.updateAllChannelsToDefaultThreshold?.success) {
              notify(
                "success",
                "Successfully update default channel threshold values."
              );
            } else {
              console.error(
                response.data?.updateAllChannelsToDefaultThreshold?.message
              );
            }
            return response.data?.updateAllChannelsToDefaultThreshold?.success;
          } catch (error) {
            console.error(error);
            return false;
          } finally {
            setModalSpinner(false);
          }
        }}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="primary"
        cancelBtnBsStyle="danger"
        confirmBtnText="Confirm"
        cancelBtnText="Cancel"
      >
        You want to Apply the default values to all Channels?
      </SweetAlert>
    );
  };

  const onChannelThresholdReset = async () => {
    setChannelThresholdChanged(false);
  };

  const onClientEnvironmentThresholdReset = async () => {
    setClientEnvironmentThresholdChanged(false);
  };

  const onChannelThresholdChange = async (key, value, label) => {
    channelThreshold[key] = value;
    setChannelThreshold(channelThreshold);
    setChannelThresholdChanged(true);
  };

  const onClientEnvironmentChange = async (key, value, label) => {
    clientEnvironmentThreshold[key] = value;
    setClientEnvironmentThreshold(clientEnvironmentThreshold);
    setClientEnvironmentThresholdChanged(true);
  };
  const handleChannelThresholdSubmit = async () => {
    try {
      setModalSpinner(true);
      let params = {
        client_id: clientId,
      };
      params = { ...channelThreshold, ...params };
      const response = await API.graphql(
        graphqlOperation(updateChannelDefaultThreshold, params)
      );
      if (response.data?.updateChannelDefaultThreshold?.success) {
        notify(
          "success",
          "Successfully update default channel threshold values."
        );
        setChannelThresholdChanged(false);
        fetchChannelDefaultThreshold();
      } else {
        console.error(response.data?.updateChannelDefaultThreshold?.message);
      }
      return response.data?.updateChannelDefaultThreshold?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setModalSpinner(false);
    }
  };
  const handleClientEnvironmentThresholdSubmit = async () => {
    try {
      setModalSpinner(true);
      let params = {
        client_id: clientId,
      };
      params = { ...clientEnvironmentThreshold, ...params };
      const response = await API.graphql(
        graphqlOperation(updateClientEnvironmentDefaultThreshold, params)
      );
      if (response.data?.updateClientEnvironmentDefaultThreshold?.success) {
        notify(
          "success",
          "Successfully update default server threshold values."
        );
        setClientEnvironmentThresholdChanged(false);
        fetchClientEnvironmentDefaultThreshold();
      } else {
        console.error(
          response.data?.updateClientEnvironmentDefaultThreshold?.message
        );
      }
      return response.data?.updateClientEnvironmentDefaultThreshold?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setModalSpinner(false);
    }
  };

  const fetchChannelDefaultThreshold = async () => {
    try {
      setModalSpinner(true);
      const params = {
        client_id: clientId,
      };
      const response = await API.graphql(
        graphqlOperation(getChannelDefaultThreshold, params)
      );
      if (response.data?.getChannelDefaultThreshold?.success) {
        setChannelThreshold(
          response.data?.getChannelDefaultThreshold?.body || {}
        );
        setChannelThresholdBackup(
          JSON.parse(
            JSON.stringify(response.data?.getChannelDefaultThreshold?.body)
          )
        );
        setChannelThresholdExists(true);
      } else {
        console.error(response.data?.getChannelDefaultThreshold?.message);
      }
      return response.data?.getChannelDefaultThreshold?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setModalSpinner(false);
    }
  };

  const fetchClientEnvironmentDefaultThreshold = async () => {
    try {
      setModalSpinner(true);
      const params = {
        client_id: clientId,
      };
      const response = await API.graphql(
        graphqlOperation(getClientEnvironmentDefaultThreshold, params)
      );

      if (response.data?.getClientEnvironmentDefaultThreshold?.success) {
        setClientEnvironmentThreshold(
          response.data?.getClientEnvironmentDefaultThreshold?.body || {}
        );
        setClientEnvironmentThresholdBackup(
          JSON.parse(
            JSON.stringify(
              response.data?.getClientEnvironmentDefaultThreshold?.body || {}
            )
          )
        );
        setClientEnvironmentThresholdExists(true);
      } else {
        setClientEnvironmentThreshold({
          mc_prune_enabled: false,
          mc_all_day: false,
          mc_prune_events: false,
        });
        console.error(
          response.data?.getClientEnvironmentDefaultThreshold?.message
        );
      }
      return response.data?.getClientEnvironmentDefaultThreshold?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setModalSpinner(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (clientThresholdType === "Channel Default Thresholds") {
      handleChannelThresholdSubmit();
    } else {
      handleClientEnvironmentThresholdSubmit();
    }
  };
  useEffect(() => {
    async function fetchData() {
      setShowFirstLoader(true);
      const filterObj = {
        offSet: 0,
        limit: currentLimit,
      };
      const promiseAllResults = await Promise.all([
        fetchList(true),
        fetchList(false),
        fetchClient(filterObj),
        fetchAllFavoriteChannels()
      ]);
      setShowFirstLoader(false);
      const isSuccess = promiseAllResults.every((value) => value);
      if (!isSuccess) {
        notify("danger");
        groupName === "internal" ? history.goBack() : setShowError(true);
      }
    }
    fetchData();
  }, []);

  if (showFirstLoader || showLoader) return <Spinner animation="grow" />;

  if (showError)
    return `Could not load the page at this moment. Please try again later.`;

  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col>
            <h2>Client Details</h2>
          </Col>
          <Col className="my-auto text-right">
            {groupName === "internal" && (
              <>
                <Link
                  className="btn btn-primary"
                  to={{
                    pathname: `/admin/clients/${clientId}/environments/create-environment`,
                  }}
                >
                  <i className="fas fa-plus"></i> Add Environment
                </Link>
                <Button
                  id="delete-client"
                  variant="danger"
                  className="ml-4 btn-outline"
                  onClick={() => {
                    if (
                      clientData?.client_environments_body?.client_environments
                        ?.length === 0
                    ) {
                      setAlert(
                        <SweetAlert
                          showCancel
                          title={"Are you sure?"}
                          onConfirm={async () => {
                            notify("info", "Deleting current Client.");
                            try {
                              const response = await API.graphql(
                                graphqlOperation(deleteClient, {
                                  client_id: clientId,
                                })
                              );
                              if (response?.data?.deleteClient?.success) {
                                notify(
                                  "success",
                                  "Successfully deleted client " + clientId
                                );
                                setTimeout(() => {
                                  history.push({
                                    pathname: `/admin/clients`,
                                  });
                                }, 3000);
                              } else {
                                notify(
                                  "danger",
                                  "Unable to delete client " + clientId
                                );
                              }
                            } catch (error) {
                              console.error("Delete Client error:", error);
                              notify(
                                "danger",
                                "Unable to delete client " + clientId
                              );
                            }
                          }}
                          onCancel={() => setAlert(null)}
                          confirmBtnBsStyle="primary"
                          cancelBtnBsStyle="danger"
                          confirmBtnText="Confirm"
                          cancelBtnText="Cancel"
                        >
                          You want to delete client "{clientData?.client_name}
                          "?
                        </SweetAlert>
                      );
                    } else {
                      notify(
                        "danger",
                        "Client cannot be deleted if there is more than one active client environment."
                      );
                    }
                  }}
                >
                  <i className="fas fa-trash-alt"></i> Delete This Client
                </Button>
              </>
            )}
            {currentUser.groupName === "external" && (
              <Button
                variant="secondary"
                className="btn-outline ml-4"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            )}
          </Col>
        </Row>
        <Row>
          <Col md="12" id="client-details-header">
            <h5>
              Name: {clientData?.client_name}
              {/* {!nameEdit ? (
                <>
                  <span>{clientData?.client_name}</span>
                  <i
                    className="fas fa-edit"
                    style={iconClass}
                    onClick={() => setNameEdit(true)}
                  ></i>
                </>
              ) : (
                <>
                  <Form.Control
                    type="text"
                    autoFocus
                    style={inputClass}
                    value={clientNameValue}
                    minLength="1"
                    maxLength="80"
                    onChange={(e) => setClientNameValue(e.target.value)}
                    name="client_name"
                  />
                  <span>
                    <i
                      className="fas fa-check"
                      style={iconClass}
                      onClick={() => {
                        if (clientNameValue.trim() === "") {
                          notify("warning", "Client Name cannot be empty");
                        } else {
                          changeClientName();
                        }
                      }}
                    ></i>
                    <i
                      className="fas fa-times"
                      style={iconClass1}
                      onClick={() => {
                        setClientNameValue(clientData?.client_name);
                        setNameEdit(false);
                      }}
                    ></i>
                  </span>
                </>
              )} */}
            </h5>
            <h5 id="client-id">Id: {clientData?.client_id}</h5>
            <h5>
              Client Status: <CustomStatus value={clientData?.client_status} />
            </h5>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              id="client-environment-action-button"
              variant="primary"
              className="btn-outline"
              onClick={() => {
                if (
                  clientData?.client_environments_body?.client_environments
                    ?.length === 0
                ) {
                  notify(
                    "danger",
                    "There is no action available when there are no active client environments"
                  );
                } else {
                  resetSelect(
                    clientData?.client_environments_body?.client_environments
                      ?.length,
                    0
                  );
                  setClientEnvironmentActionModalOpen(true);
                }
              }}
            >
              Client Environment Action
            </Button>
            <Button
              id="client-environment-default-thresholds-button"
              variant="primary"
              className="btn-outline ml-4"
              onClick={() => {
                resetSelect(
                  clientData?.client_environments_body?.client_environments
                    ?.length,
                  0
                );
                setClientThresholdsModalOpen(true);
              }}
            >
              Set Default Thresholds
            </Button>
            <Link
              id="mc-server-info-button"
              className="btn btn-primary btn-outline ml-4"
              to={{
                pathname: `/admin/mc-server-info`,
                state: {
                  client_ids: [
                    {
                      label: clientData?.client_name,
                      value: clientData?.client_id,
                    },
                  ],
                },
              }}
            >
              MC Server Info
            </Link>
            <Link
              id="manage-licensing-button"
              className="btn btn-primary btn-outline ml-4"
              to={{
                pathname: `/admin/clients/${clientData?.client_id}/manage-licensing`,
              }}
            >
              Manage Licensing
            </Link>
            <Link
              id="client-message-storage-button"
              className="btn btn-primary btn-outline ml-4"
              to={{
                pathname: `/admin/clients/${clientId}/message-storage`,
              }}
            >
              Message Storage
            </Link>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <Card.Title>Metrics Search</Card.Title>
                <Card.Subtitle>
                  {jobRanAt
                    ? `Last calculated at ${new Date(jobRanAt).toLocaleString(
                      "en-US"
                    )}`
                    : `No data to show for selected date(s)`}
                </Card.Subtitle>
              </Card.Header>
              <Card.Body>
                <Row className="align-items-end">
                  <Col md="4" sm="6">
                    <label>Select Date(s):</label>
                    <CustomMultiDateSelect
                      className="react-select primary"
                      classNamePrefix="react-select"
                      style={{ width: "100%" }}
                      value={metricDateList}
                      onChange={setMetricDateList}
                    ></CustomMultiDateSelect>
                  </Col>
                  {groupName === "internal" && (
                    <Col md="3" sm="6" style={{ marginBottom: "8px" }}>
                      <Form.Check>
                        <Form.Check.Label>
                          <Form.Check.Input
                            type="checkbox"
                            checked={showInactiveEnvironments}
                            onChange={handleIncludeInactiveClientEnvironments}
                          ></Form.Check.Input>
                          <span className="form-check-sign"></span>
                          Inactive Environments
                        </Form.Check.Label>
                      </Form.Check>
                    </Col>
                  )}
                  <Col md="4" sm="6">
                    <label>Client Environment Filter</label>
                    <Select
                      className="react-select primary"
                      classNamePrefix="react-select"
                      name="clientEnvironmentFilter"
                      closeMenuOnSelect={false}
                      isMulti
                      styles={customEllipsisMultiSelectStyles}
                      components={{ MultiValue: MultiValueWithCustomTooltip }}
                      value={selectedEnvironments}
                      onChange={(clientEnv) => {
                        setSelectedEnvironments(clientEnv ? clientEnv : []);
                      }}
                      options={
                        showInactiveEnvironments
                          ? inactiveEnvironmentOptions
                          : activeEnvironmentOptions
                      }
                      isClearable={false}
                      placeholder="None Selected"
                    />
                  </Col>
                  <Col md="1" sm="6">
                    <Button
                      variant="primary"
                      onClick={async () => {
                        setSortValue("Name");
                        setAscOrDescValue("ASC");
                        setShowFirstLoader(true);
                        const filterObj = {
                          offSet: 0,
                          limit: 50,
                        };
                        const promiseAllResults = await Promise.all([
                          fetchClient(filterObj),
                          fetchAllFavoriteChannels()
                        ]);
                        setShowFirstLoader(false);
                        const isSuccess = promiseAllResults.every((value) => value);
                        if (isSuccess) notify("success");
                        else notify("danger");
                      }}
                    >
                      {" "}
                      Search
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Favorite Channels Section */}
        {favoriteChannelsData.length > 0 &&
          <Row className="mt-4">
            <Col>
              <ReactTable
                data={favoriteChannelsData.map((channel, index) => {
                  return {
                    id: channel.channel_id,
                    status: <CustomStatus value={channel.channel_status} />,
                    channel_name: channel.enabled ? (
                      channel.spoke_type === 0 ? (
                        <Link
                          to={{
                            pathname: `/admin/clients/${clientId}/environments/${channel.client_environment_id}/channels-legacy/${channel.channel_id}`,
                          }}
                          key={index}
                        >
                          {channel.channel_name}
                        </Link>
                      ) : (
                        <Link
                          to={{
                            pathname: `/admin/clients/${clientId}/environments/${channel.client_environment_id}/channels/${channel.channel_id}`,
                          }}
                          key={index}
                        >
                          {channel.channel_name}
                        </Link>
                      )
                    ) : (
                      channel.channel_name
                    ),
                    action: (
                      <Link
                        to={{
                          pathname: "/admin/find-or-reprocess",
                          state: {
                            ...channel,
                            channelName: channel.channel_name,
                            channelId: channel.channel_id,
                            clientId: clientId,
                            clientName: clientNameValue,
                            clientEnvironmentId: channel.client_environment_id,
                            clientEnvironmentName:
                              channel.client_environment_name,
                          },
                        }}
                      >
                        Go to Messages
                      </Link>
                    ),
                    queued_messages: channel.queued_messages,
                    client_environment_name: channel.client_environment_name,
                    channel_group_name: channel.channel_group_name,
                    received_messages: channel.received_messages,
                    sent_messages: channel.sent_messages,
                    filtered_messages: channel.filtered_messages,
                    errored_messages: channel.errored_messages,
                    support_tier:
                      channel.support_tier,
                    suppress_support_event: channel.suppress_support_event,
                    spoke_type: channel.spoke_type,
                    avg_process_time: channel.avg_process_time,
                    mc_auth_type: authTypeMap[channel.mc_auth_type],
                    last_reviewed_by_support: channel.last_reviewed_by_support
                      ? new Date(
                        channel.last_reviewed_by_support
                      ).toLocaleString()
                      : "Not Reviewed",
                  };
                })}
                totalCount={favoriteChannelsData.length}
                functionCallBack={fetchAllFavoriteChannels}
                setStateForPagnination={setFavStateForPagnination}
                currentLimit={favCurrentLimit}
                currentOffset={favCurrentOffset}
                ascOrDescValue={favAscOrDescValue}
                sortValue={favSortValue}
                numberOfRowsData={numberOfRowsData}
                columnMapping={favoriteColumnMapping}
                tableName="Favorite Channels"
                CSVFileName={
                  "FavoriteChannels_" + clientData?.client_id + "_Export"
                }
                CSVHeaders={favoriteHeaders}
                notify={notify}

                columns={[
                  {
                    Header: "Status",
                    accessor: "status",
                    filterable: false,
                  },
                  {
                    Header: "Support Tier",
                    accessor: (row) => {
                      if (row.spoke_type === 0) {
                        return mapSupportTier(row.support_tier);
                      } else {
                        return ''
                      }
                    },
                    filterable: false,
                  },
                  {
                    Header: "Group",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      return (
                        <CustomTooltip
                          id={`channel-group-id-${row.id}`}
                          value={row.channel_group_name}
                          placement="right"
                        />
                      );
                    },
                    accessor: "channel_group_name",
                    filterable: false,
                    minWidth: 200,
                  },
                  {
                    Header: "Client Environment Name",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      return (
                        <CustomTooltip
                          id={`client_environment-id-${row.id}`}
                          value={row.client_environment_name}
                          placement="right"
                        />
                      );
                    },
                    accessor: "client_environment_name",
                    filterable: false,
                    minWidth: 200,
                  },
                  {
                    Header: "Channel Name",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      return (
                        <CustomTooltip
                          id={`channel-id-${row.id}`}
                          value={row.channel_name}
                          placement="right"
                        />
                      );
                    },
                    accessor: "channel_name",
                    filterable: false,
                    minWidth: 200,
                  },
                  {
                    Header: "View Messages",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      if (row.spoke_type === 0) {
                        return (
                          <CustomTooltip
                            id={`view-messages-${row.id}`}
                            value={row.action}
                            placement="left"
                          />
                        );
                      } else {
                        return ''
                      }
                    },
                    accessor: "action",
                    sortable: false,
                    filterable: false,
                    minWidth: 200,
                  },
                  {
                    Header: "Blocked Email Alerts",
                    accessor: (row) => {
                      return row.suppress_support_event ? "Yes" : "No";
                    },
                    filterable: false,
                    minWidth: 220,
                  },
                  {
                    Header: "Received",
                    accessor: (row) => {
                      return row.received_messages ? row.received_messages : 0;
                    },
                    filterable: false,
                    maxWidth: 100,
                  },
                  {
                    Header: "Queued",
                    accessor: (row) => {
                      return row.queued_messages ? row.queued_messages : 0;
                    },
                    filterable: false,
                    maxWidth: 100,
                  },
                  {
                    Header: "Filtered",
                    accessor: (row) => {
                      return row.filtered_messages ? row.filtered_messages : 0;
                    },
                    filterable: false,
                    maxWidth: 100,
                  },
                  {
                    Header: "Errored",
                    accessor: (row) => {
                      return row.errored_messages ? row.errored_messages : 0;
                    },
                    filterable: false,
                    maxWidth: 100,
                  },
                  {
                    Header: "Sent/Transformed",
                    accessor: (row) => {
                      return row.sent_messages ? row.sent_messages : 0;
                    },
                    filterable: false,
                    minWidth: 160,
                  },
                  {
                    Header: "Avg Process Time (sec)",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      return (
                        <CustomTooltip
                          id={`avg-process-time-${row.channel_id}`}
                          value={Math.round(10000 * row.avg_process_time) / 10000}
                          placement="left"
                        />
                      );
                    },
                    accessor: "avg_process_time",
                    filterable: false,
                    minWidth: 200,
                  },
                  {
                    Header: "Auth Type",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      return (
                        <CustomTooltip
                          id={`mc-auth-type-${row.channel_id}`}
                          value={row.mc_auth_type}
                          placement="left"
                        />
                      );
                    },
                    accessor: "mc_auth_type",
                    filterable: false,
                    minWidth: 160,
                  },
                  {
                    Header: "Last Reviewed",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      return (
                        <CustomTooltip
                          id={`last-reviewed-${row.channel_id}`}
                          value={row.last_reviewed_by_support}
                          placement="left"
                        />
                      );
                    },
                    accessor: "last_reviewed_by_support",
                    filterable: false,
                    minWidth: 200,
                  },
                ]}
                hidePagination={false}
              />
            </Col>
          </Row>
        }
        <Row>
          {groupName === "internal" && (
            <Col md="12">
              <ReactTable
                data={tableData}
                totalCount={totalCount}
                functionCallBack={fetchClient}
                setStateForPagnination={setStateForPagnination}
                currentLimit={currentLimit}
                currentOffset={currentOffset}
                ascOrDescValue={ascOrDescValue}
                sortValue={sortValue}
                numberOfRowsData={numberOfRowsData}
                columnMapping={columnMapping}
                tableName="Environments"
                CSVFileName={
                  "Environments_" + clientData?.client_id + "_Export"
                }
                CSVHeaders={headers}
                notify={notify}
                columns={[
                  {
                    Header: "Status",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      if (row.active)
                        return (
                          <CustomStatus value={row.client_environment_status} />
                        );
                    },
                    accessor: "client_environment_status",
                    filterable: false,
                    showTooltip: false,
                    placement: "right",
                  },
                  {
                    Header: "Environment",
                    Cell: (cell) => {
                      const row = cell.row.original;
                      if (row.active) {
                        return (
                          <Link
                            to={{
                              pathname: `/admin/clients/${clientId}/environments/${row.client_environment_id}`,
                            }}
                          >
                            {row.client_environment_name}
                          </Link>
                        );
                      } else {
                        return <span>{row.client_environment_name}</span>;
                      }
                    },
                    accessor: "client_environment_name",
                    filterable: false,
                    showTooltip: true,
                    minWidth: 200,
                  },
                  {
                    Header: "Deploy Status",
                    accessor: "deploy_status",
                    filterable: false,
                    showTooltip: true,
                  },
                  {
                    Header: "Blocked Email Alerts",
                    accessor: (row) => {
                      return row.suppress_support_event ? "Yes" : "No";
                    },
                    filterable: false,
                    showTooltip: false,
                    minWidth: 220,
                  },
                  {
                    Header: "Spoke Name",
                    accessor: "spoke_name",
                    filterable: false,
                    showTooltip: true,
                  },
                  {
                    Header: "Spoke Type",
                    accessor: (row) => {
                      return row.spoke_type === 0 ? "Fully Managed" : "Remote";
                    },
                    filterable: false,
                    showTooltip: false,
                  },
                  {
                    Header: "Region",
                    accessor: "region",
                    filterable: false,
                    showTooltip: true,
                  },
                  {
                    Header: "AWS Account",
                    accessor: "aws_account",
                    filterable: false,
                    showTooltip: true,
                  },
                  {
                    Header: "Last Environment Update",
                    accessor: "lastEvaluated",
                    filterable: false,
                    showTooltip: true,
                    minWidth: 210,
                  },
                ]}
                hidePagination={false}
              />
            </Col>
          )}
          <Col md="12" id="environment-metrics-data-grid">
            <ReactTable
              data={tableData}
              totalCount={totalCount}
              functionCallBack={fetchClient}
              setStateForPagnination={setStateForPagnination}
              currentLimit={currentLimit}
              currentOffset={currentOffset}
              ascOrDescValue={ascOrDescValue}
              sortValue={sortValue}
              numberOfRowsData={numberOfRowsData}
              columnMapping={columnMapping}
              tableName="Environment Metrics"
              CSVHeaders={metricsHeaders}
              CSVFileName={
                "Environment_Metrics_" + clientData?.client_id + "_Export"
              }
              notify={notify}
              columns={[
                {
                  Header: "Status",
                  Cell: (cell) => {
                    const row = cell.row.original;
                    return (
                      <CustomStatus value={row.client_environment_status} />
                    );
                  },
                  accessor: "client_environment_status",
                  filterable: false,
                  showTooltip: false,
                },
                {
                  Header: "Environment",
                  Cell: (cell) => {
                    const row = cell.row.original;
                    return (
                      <Link
                        to={{
                          pathname: `/admin/clients/${clientId}/environments/${row.client_environment_id}`,
                        }}
                      >
                        {row.client_environment_name}
                      </Link>
                    );
                  },
                  accessor: "client_environment_name",
                  filterable: false,
                  showTooltip: true,
                  minWidth: 200,
                },
                ...(groupName != "internal"
                  ? [
                    {
                      Header: "Blocked Email Alerts",
                      accessor: (row) =>
                        row.suppress_support_event ? "Yes" : "No",
                      filterable: false,
                      showTooltip: false,
                      minWidth: 220,
                    },
                  ]
                  : []),
                {
                  Header: "Channels",
                  accessor: "channels_count",
                  filterable: false,
                  showTooltip: false,
                },
                {
                  Header: "Received",
                  accessor: (row) => {
                    return row.received_messages ? row.received_messages : 0;
                  },
                  filterable: false,
                  showTooltip: false,
                },
                {
                  Header: "Queued",
                  accessor: (row) => {
                    return row.queued_messages ? row.queued_messages : 0;
                  },
                  filterable: false,
                  showTooltip: false,
                },
                {
                  Header: "Filtered",
                  accessor: (row) => {
                    return row.filtered_messages ? row.filtered_messages : 0;
                  },
                  filterable: false,
                  showTooltip: false,
                },
                {
                  Header: "Errored",
                  accessor: (row) => {
                    return row.errored_messages ? row.errored_messages : 0;
                  },
                  filterable: false,
                  showTooltip: false,
                },
                {
                  Header: "Sent/Transformed",
                  accessor: (row) => {
                    return row.sent_messages ? row.sent_messages : 0;
                  },
                  filterable: false,
                  showTooltip: false,
                  minWidth: 180,
                },
                {
                  Header: "Avg Process Time (sec)",
                  accessor: (row) => {
                    return Math.round(10000 * row.avg_process_time) / 10000;
                  },
                  filterable: false,
                  showTooltip: false,
                  minWidth: 180,
                },
              ]}
              hidePagination={false}
            />
          </Col>
        </Row>

        <Row>
          <SupportEvents
            inputProps={{
              client_id: clientData?.client_id,
              client_name: clientData?.client_name,
            }}
          ></SupportEvents>
        </Row>
        <Modal
          toggle={() =>
            setClientEnvironmentActionModalOpen(
              !clientEnvironmentActionModalOpen
            )
          }
          size="lg"
          isOpen={clientEnvironmentActionModalOpen}
          scrollable={false}
        >
          <div className="modal-header">
            <div
              className="modal-title"
              id="clientEnvironmentActionOptionsList"
            >
              <h5>Select Client Environment Action Options</h5>
              {!allEnvReady && (
                <h5 className="text-danger">
                  *Note: One or more environments are currently deploying and
                  can't be changed.
                </h5>
              )}
            </div>
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setClientEnvironmentActionModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ModalBody>
            <Row>
              <Col>
                <DropdownButton
                  id="client-environment-action-all-dropdown"
                  variant="primary"
                  disabled={!allEnvReady}
                  onSelect={(key) =>
                    handleClientEnvironmentActionAllSelect(
                      key,
                      clientData?.client_environments_body?.client_environments
                    )
                  }
                  title="Apply to All "
                >
                  {actionOptionsList.map((item, index) => {
                    return (
                      <Dropdown.Item key={index} eventKey={item.key}>
                        {item.value}
                      </Dropdown.Item>
                    );
                  })}
                </DropdownButton>
              </Col>
              <Col className="text-right">
                {currentUser.groupName === "external" && (
                  <Button
                    variant="secondary"
                    className="btn-outline"
                    onClick={() => {
                      setSteps(clientEnvironmentActionModalSteps);
                      setCurrentStep(0);
                      setIsOpen(true);
                    }}
                  >
                    <i className="fas fa-desktop"></i> View Our Tour Guide
                  </Button>
                )}
              </Col>
            </Row>
            <Table>
              <thead>
                <tr>
                  <th>Client Environment</th>
                  <th>Environment Type</th>
                  <th id="select-action-to-take-column">
                    Select Action to Take
                  </th>
                </tr>
              </thead>
              <tbody>
                {clientData?.client_environments_body?.client_environments.map(
                  (client_env, index) => {
                    if (client_env.spoke_type == 0) {
                      return (
                        <tr key={index}>
                          <td>{client_env.client_environment_name}</td>
                          <td>Fully Managed</td>
                          <td>
                            <DropdownButton
                              id={`dropdown-basic-button-${index}`}
                              variant="primary"
                              className="floatRight"
                              disabled={
                                !isStatusInAccessibleState(
                                  client_env.deploy_status
                                )
                              }
                              onSelect={(key, event) =>
                                handleMultiSelect(key, event, index)
                              }
                              title={
                                multiSelected[index]?.value ||
                                actionOptionsListInternalEnvironment[0].value
                              }
                            >
                              {actionOptionsListInternalEnvironment.map(
                                (item, index) => {
                                  return (
                                    <Dropdown.Item
                                      key={index}
                                      eventKey={item.key}
                                    >
                                      {item.value}
                                    </Dropdown.Item>
                                  );
                                }
                              )}
                            </DropdownButton>
                          </td>
                        </tr>
                      );
                    } else {
                      return (
                        <tr key={index}>
                          <td>{client_env.client_environment_name}</td>
                          <td>Remote</td>
                          <td>
                            <DropdownButton
                              id={`dropdown-basic-button-${index}`}
                              variant="primary"
                              className="floatRight"
                              onSelect={(key, event) =>
                                handleMultiSelect(key, event, index)
                              }
                              title={
                                multiSelected[index]?.value ||
                                actionOptionsListExternalEnvironment[0].value
                              }
                            >
                              {actionOptionsListExternalEnvironment.map(
                                (item, index) => {
                                  return (
                                    <Dropdown.Item
                                      key={index}
                                      eventKey={item.key}
                                    >
                                      {item.value}
                                    </Dropdown.Item>
                                  );
                                }
                              )}
                            </DropdownButton>
                          </td>
                        </tr>
                      );
                    }
                  }
                )}
              </tbody>
            </Table>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              id="client-environment-action-cancel-button"
              onClick={() => setClientEnvironmentActionModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              id="client-environment-action-submit-button"
              onClick={() => {
                notify(
                  "info",
                  "Creating client environment action requests...",
                );
                handleClientEnvironmentActionSubmit(
                  clientData.client_id,
                  clientData?.client_environments_body?.client_environments,
                  currentUser.userId
                );
                setClientEnvironmentActionModalOpen(false);
              }}
            >
              Submit
            </Button>
          </ModalFooter>
        </Modal>
        <Modal
          toggle={() =>
            setClientThresholdsModalOpen(!clientThresholdsModalOpen)
          }
          size="xl"
          isOpen={clientThresholdsModalOpen}
          scrollable={false}
        >
          <div className="modal-header">
            <div
              className="modal-title"
              id="clientEnvironmentActionOptionsList"
            >
              <h5>Select Default Threshold Type</h5>
              {!allEnvReady && (
                <h5 className="text-danger">
                  *Note: One or more environments are currently deploying and
                  can't be changed.
                </h5>
              )}
            </div>
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setClientThresholdsModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <Row>
                <Col>
                  <DropdownButton
                    id="default-threshold-type-dropdown"
                    variant="primary"
                    className="floatRight"
                    disabled={!allEnvReady}
                    onSelect={(key) => {
                      setClientThresholdType(key);
                      if (key === "Channel Default Thresholds") {
                        fetchChannelDefaultThreshold();
                      } else {
                        fetchClientEnvironmentDefaultThreshold();
                      }
                    }}
                    title={
                      clientThresholdType || "Select Default Threshold Type"
                    }
                  >
                    {clientThresholdTypes.map((item, index) => {
                      return (
                        <Dropdown.Item key={index} eventKey={item.key}>
                          {item.value}
                        </Dropdown.Item>
                      );
                    })}
                  </DropdownButton>
                </Col>
                <Col className="text-right">
                  {currentUser.groupName === "external" && (
                    <Button
                      variant="secondary"
                      className="btn-outline"
                      onClick={() => {
                        setSteps(clientEnvironmentDefaultThresholdModalSteps);
                        setCurrentStep(0);
                        setIsOpen(true);
                      }}
                    >
                      <i className="fas fa-desktop"></i> View Our Tour Guide
                    </Button>
                  )}
                </Col>
              </Row>
              {modalSpinner && <Spinner animation="grow" />}
              {clientThresholdType &&
                clientThresholdType === "Channel Default Thresholds" &&
                !modalSpinner && (
                  <Card>
                    <Card.Body>
                      <ChannelThreshold
                        channelData={channelThreshold}
                        channelsBackUp={channelThresholdBackup}
                        addOrUpdate="Update"
                        index={0}
                        onChange={onChannelThresholdChange}
                        onReset={onChannelThresholdReset}
                        isEdit={true}
                        isInternalSpokeType={true}
                      />
                    </Card.Body>
                  </Card>
                )}
              {clientThresholdType &&
                clientThresholdType ===
                "Client Environment Default Thresholds" &&
                !modalSpinner && (
                  <UpdateMirthConnectConfiguration
                    thresholds={clientEnvironmentThreshold}
                    thresholdConfiguration={clientEnvironmentThresholdBackup}
                    onChange={onClientEnvironmentChange}
                    onReset={onClientEnvironmentThresholdReset}
                  />
                )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="secondary"
                id="cancel-default-threshold-button"
                onClick={() => setClientThresholdsModalOpen(false)}
              >
                Cancel
              </Button>
              <div>
                {clientThresholdType &&
                  clientThresholdType ===
                  "Client Environment Default Thresholds" &&
                  !modalSpinner && (
                    <Button
                      variant="primary"
                      style={{ marginRight: "20px" }}
                      onClick={functionToChangeClientEnvironmentThreshold}
                      disabled={
                        clientEnvironmentThresholdChanged ||
                        !clientEnvironmentThresholdExists ||
                        totalCount == 0
                      }
                    >
                      Apply above default threshold to all Client Environments
                    </Button>
                  )}
                {clientThresholdType &&
                  clientThresholdType === "Channel Default Thresholds" &&
                  !modalSpinner && (
                    <Button
                      variant="primary"
                      style={{ marginRight: "20px" }}
                      onClick={functionToChangeChannelThreshold}
                      disabled={
                        channelThresholdChanged ||
                        !channelThresholdExists ||
                        totalChannelCount == 0
                      }
                    >
                      Apply above default threshold to all Channels
                    </Button>
                  )}
                <Button
                  variant="primary"
                  type="submit"
                  id="save-default-threshold-button"
                  disabled={
                    !clientEnvironmentThresholdChanged &&
                    !channelThresholdChanged
                  }
                >
                  Save Default Thresholds
                </Button>
              </div>
            </ModalFooter>
          </form>
        </Modal>
      </Container>
    </>
  );
}

export default Client;
